import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, BookOpen, Database, Search, Sparkles } from 'lucide-react';
import { Link } from 'react-router';
import ModuleHeader from './ModuleHeader';
import { getChapter } from '../services/bibleReaderService';
import { getBookName } from '../data/bibleData';

const topics = [
    { id: 'peace', label: 'Peace', prompt: 'For an anxious or unsettled heart', passages: [['JHN', 14, 27], ['PHP', 4, 7], ['PSA', 4, 8]] },
    { id: 'hope', label: 'Hope', prompt: 'For days when the way forward feels dim', passages: [['JER', 29, 11], ['ROM', 15, 13], ['PSA', 42, 11]] },
    { id: 'faith', label: 'Faith', prompt: 'For trusting God one step at a time', passages: [['HEB', 11, 1], ['PRO', 3, 5, 6], ['MRK', 11, 24]] },
    { id: 'love', label: 'Love', prompt: 'For receiving and practicing steadfast love', passages: [['1CO', 13, 4, 7], ['JHN', 3, 16], ['1JN', 4, 19]] }
];

const ScriptureFinder = () => {
    const [topicId, setTopicId] = useState('peace');
    const [query, setQuery] = useState('');
    const [passages, setPassages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const activeTopic = topics.find((topic) => topic.id === topicId);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        Promise.all(activeTopic.passages.map(async ([book, chapterNumber, startVerse, endVerse = startVerse]) => {
            const chapter = await getChapter(book, chapterNumber, 'WEBP');
            const verses = chapter.verses.filter((verse) => verse.number >= startVerse && verse.number <= endVerse);
            return {
                id: `${book}-${chapterNumber}-${startVerse}-${endVerse}`,
                book,
                bookName: getBookName(book),
                chapter: chapterNumber,
                startVerse,
                endVerse,
                reference: `${getBookName(book)} ${chapterNumber}:${startVerse}${endVerse !== startVerse ? `–${endVerse}` : ''}`,
                text: verses.map((verse) => verse.text).join(' ')
            };
        })).then((loadedPassages) => {
            if (active) setPassages(loadedPassages);
        }).catch((loadError) => {
            if (active) setError(loadError.message || 'Passages could not be loaded.');
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [activeTopic]);

    const visiblePassages = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        if (!normalizedQuery) return passages;
        return passages.filter((passage) =>
            passage.reference.toLowerCase().includes(normalizedQuery) ||
            passage.text.toLowerCase().includes(normalizedQuery)
        );
    }, [passages, query]);

    return (
        <div className="scripture-module-shell">
            <ModuleHeader title="Scripture Explorer" eyebrow="Find a passage" icon={BookOpen} tone="blue" />
            <main className="scripture-module-main">
                <section className="scripture-hero">
                    <div>
                        <p className="shared-kicker"><Sparkles size={14} /> Scripture for the moment</p>
                        <h1>What does your heart need today?</h1>
                        <p>Begin with a theme and open each passage in the full Bible Reader when you are ready.</p>
                    </div>
                    <div className="scripture-search">
                        <Search size={19} />
                        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find within these passages" />
                    </div>
                </section>

                <nav className="scripture-topics" aria-label="Scripture topics">
                    {topics.map((topic) => (
                        <button key={topic.id} className={topicId === topic.id ? 'is-active' : ''} onClick={() => { setTopicId(topic.id); setQuery(''); }}>
                            <strong>{topic.label}</strong><span>{topic.prompt}</span>
                        </button>
                    ))}
                </nav>

                <section className="scripture-results">
                    <header>
                        <div><p className="shared-kicker">Selected theme</p><h2>{activeTopic.label}</h2><span>{activeTopic.prompt}</span></div>
                        <p><Database size={15} /> Verified WEB text</p>
                    </header>

                    {loading ? (
                        <div className="scripture-state">Opening verified passages…</div>
                    ) : error ? (
                        <div className="scripture-state">{error}</div>
                    ) : !visiblePassages.length ? (
                        <div className="scripture-state">No passages in this theme match your search.</div>
                    ) : (
                        <div className="scripture-passage-grid">
                            {visiblePassages.map((passage) => (
                                <article className="scripture-passage-card" key={passage.id}>
                                    <div><BookOpen size={17} /><span>World English Bible</span></div>
                                    <h3>{passage.reference}</h3>
                                    <blockquote>“{passage.text}”</blockquote>
                                    <Link to={`/bible-reader/${passage.book}?chapter=${passage.chapter}&verse=${passage.startVerse}`}>
                                        Read in context <ArrowRight size={16} />
                                    </Link>
                                </article>
                            ))}
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
};

export default ScriptureFinder;
